import { EventEmitter } from 'events';
import { MongoClient, Db, Collection } from 'mongodb';

export interface DatabaseConfig {
  uri: string;
  database?: string;
  collections: {
    messages?: string;
    chats?: string;
    contacts?: string;
    sessions?: string;
    [key: string]: string | undefined;
  };
  fieldMapping?: FieldMapping;
  transformers?: TransformerMap;
  conflictResolution?: ConflictResolutionStrategy;
  batchSize?: number;
  syncInterval?: number;
}

export interface FieldMapping {
  [originalField: string]: string | FieldMappingConfig;
}

export interface FieldMappingConfig {
  target: string;
  transform?: (value: any) => any;
  required?: boolean;
  default?: any;
}

export interface TransformerMap {
  [fieldName: string]: (value: any, document?: any) => any;
}

export type ConflictResolutionStrategy = 'overwrite' | 'merge' | 'skip' | 'custom';

export interface SyncOptions {
  upsert?: boolean;
  batchSize?: number;
  skipValidation?: boolean;
  transformers?: TransformerMap;
  conflictResolution?: ConflictResolutionStrategy;
}

export interface SyncResult {
  processed: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: Array<{ document: any; error: string }>;
}

export abstract class DatabaseAdapter extends EventEmitter {
  protected config: Required<DatabaseConfig>;

  constructor(config: DatabaseConfig) {
    super();
    this.config = {
      database: 'whatsapp',
      fieldMapping: {},
      transformers: {},
      conflictResolution: 'merge',
      batchSize: 100,
      syncInterval: 5000,
      ...config
    };
  }

  abstract connect(): Promise<void>;
  abstract disconnect(): Promise<void>;
  abstract isConnected(): boolean;
  abstract sync(collectionName: string, documents: any[], options?: SyncOptions): Promise<SyncResult>;
  abstract query(collectionName: string, filter: any, options?: any): Promise<any[]>;
  abstract findOne(collectionName: string, filter: any): Promise<any>;
  abstract insertOne(collectionName: string, document: any): Promise<any>;
  abstract updateOne(collectionName: string, filter: any, update: any): Promise<any>;
  abstract deleteOne(collectionName: string, filter: any): Promise<any>;

  /**
   * Transform document according to field mapping
   */
  protected transformDocument(document: any, mapping?: FieldMapping): any {
    const fieldMapping = mapping || this.config.fieldMapping;
    const transformed: any = {};

    for (const [originalField, mappingConfig] of Object.entries(fieldMapping)) {
      const config = typeof mappingConfig === 'string' 
        ? { target: mappingConfig } 
        : mappingConfig;

      const value = this.getNestedValue(document, originalField);
      
      if (value !== undefined || config.required) {
        const finalValue = config.transform 
          ? config.transform(value) 
          : (value !== undefined ? value : config.default);
        
        this.setNestedValue(transformed, config.target, finalValue);
      }
    }

    // Apply transformers
    for (const [fieldName, transformer] of Object.entries(this.config.transformers)) {
      if (this.hasNestedValue(transformed, fieldName)) {
        const value = this.getNestedValue(transformed, fieldName);
        this.setNestedValue(transformed, fieldName, transformer(value, transformed));
      }
    }

    return transformed;
  }

  /**
   * Get nested value from object using dot notation
   */
  protected getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  /**
   * Set nested value in object using dot notation
   */
  protected setNestedValue(obj: any, path: string, value: any): void {
    const keys = path.split('.');
    const lastKey = keys.pop()!;
    
    const target = keys.reduce((current, key) => {
      if (current[key] === undefined) {
        current[key] = {};
      }
      return current[key];
    }, obj);
    
    target[lastKey] = value;
  }

  /**
   * Check if nested value exists
   */
  protected hasNestedValue(obj: any, path: string): boolean {
    return this.getNestedValue(obj, path) !== undefined;
  }

  /**
   * Merge two objects with conflict resolution
   */
  protected mergeDocuments(existing: any, incoming: any, strategy: ConflictResolutionStrategy): any {
    switch (strategy) {
      case 'overwrite':
        return { ...existing, ...incoming };
      
      case 'merge':
        return this.deepMerge(existing, incoming);
      
      case 'skip':
        return existing;
      
      case 'custom':
        return this.customMerge(existing, incoming);
      
      default:
        return this.deepMerge(existing, incoming);
    }
  }

  /**
   * Deep merge two objects
   */
  protected deepMerge(target: any, source: any): any {
    const result = { ...target };
    
    for (const key in source) {
      if (source[key] !== null && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        result[key] = this.deepMerge(result[key] || {}, source[key]);
      } else {
        result[key] = source[key];
      }
    }
    
    return result;
  }

  /**
   * Custom merge logic - override in subclasses
   */
  protected customMerge(existing: any, incoming: any): any {
    return this.deepMerge(existing, incoming);
  }
}

export class MongoDBAdapter extends DatabaseAdapter {
  private client: MongoClient | null = null;
  private db: Db | null = null;

  async connect(): Promise<void> {
    try {
      this.client = new MongoClient(this.config.uri);
      await this.client.connect();
      this.db = this.client.db(this.config.database);
      
      this.emit('connected');
      console.log(`Connected to MongoDB: ${this.config.database}`);
    } catch (error) {
      this.emit('error', error);
      throw new Error(`Failed to connect to MongoDB: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async disconnect(): Promise<void> {
    if (this.client) {
      await this.client.close();
      this.client = null;
      this.db = null;
      this.emit('disconnected');
    }
  }

  isConnected(): boolean {
    return this.client !== null && this.db !== null;
  }

  private getCollection(name: string): Collection {
    if (!this.db) {
      throw new Error('Database not connected');
    }
    
    const collectionName = this.config.collections[name] || name;
    return this.db.collection(collectionName);
  }

  async sync(collectionName: string, documents: any[], options: SyncOptions = {}): Promise<SyncResult> {
    const collection = this.getCollection(collectionName);
    const result: SyncResult = {
      processed: 0,
      inserted: 0,
      updated: 0,
      skipped: 0,
      errors: []
    };

    const batchSize = options.batchSize || this.config.batchSize;
    const upsert = options.upsert ?? true;

    for (let i = 0; i < documents.length; i += batchSize) {
      const batch = documents.slice(i, i + batchSize);
      
      for (const doc of batch) {
        try {
          result.processed++;

          // Transform document
          const transformed = this.transformDocument(doc, this.config.fieldMapping);
          
          if (upsert) {
            // Try to find existing document
            const existing = await this.findExistingDocument(collection, transformed);
            
            if (existing) {
              // Apply conflict resolution
              const merged = this.mergeDocuments(
                existing, 
                transformed, 
                options.conflictResolution || this.config.conflictResolution
              );
              
              if (JSON.stringify(merged) !== JSON.stringify(existing)) {
                await collection.updateOne({ _id: existing._id }, { $set: merged });
                result.updated++;
                this.emit('document:updated', { original: doc, transformed: merged });
              } else {
                result.skipped++;
              }
            } else {
              // Insert new document
              await collection.insertOne(transformed);
              result.inserted++;
              this.emit('document:inserted', { original: doc, transformed });
            }
          } else {
            // Direct insert
            await collection.insertOne(transformed);
            result.inserted++;
            this.emit('document:inserted', { original: doc, transformed });
          }

        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          result.errors.push({
            document: doc,
            error: errorMessage
          });
          this.emit('document:error', { document: doc, error });
        }
      }
    }

    this.emit('sync:completed', result);
    return result;
  }

  private async findExistingDocument(collection: Collection, document: any): Promise<any> {
    // Try to find by common unique fields
    const uniqueFields = ['key.id', 'messageId', 'id', 'key.remoteJid'];
    
    for (const field of uniqueFields) {
      const value = this.getNestedValue(document, field);
      if (value) {
        const existing = await collection.findOne({ [field]: value });
        if (existing) {
          return existing;
        }
      }
    }
    
    return null;
  }

  async query(collectionName: string, filter: any, options: any = {}): Promise<any[]> {
    const collection = this.getCollection(collectionName);
    return await collection.find(filter, options).toArray();
  }

  async findOne(collectionName: string, filter: any): Promise<any> {
    const collection = this.getCollection(collectionName);
    return await collection.findOne(filter);
  }

  async insertOne(collectionName: string, document: any): Promise<any> {
    const collection = this.getCollection(collectionName);
    const transformed = this.transformDocument(document);
    return await collection.insertOne(transformed);
  }

  async updateOne(collectionName: string, filter: any, update: any): Promise<any> {
    const collection = this.getCollection(collectionName);
    return await collection.updateOne(filter, update);
  }

  async deleteOne(collectionName: string, filter: any): Promise<any> {
    const collection = this.getCollection(collectionName);
    return await collection.deleteOne(filter);
  }

  /**
   * Create indexes for better performance
   */
  async createIndexes(collectionName: string, indexes: any[]): Promise<void> {
    const collection = this.getCollection(collectionName);
    
    for (const index of indexes) {
      await collection.createIndex(index.key, index.options || {});
    }
    
    this.emit('indexes:created', { collection: collectionName, count: indexes.length });
  }

  /**
   * Get collection statistics
   */
  async getStats(collectionName: string): Promise<any> {
    const collection = this.getCollection(collectionName);
    const count = await collection.countDocuments();
    return { count, name: collectionName };
  }
}

/**
 * Built-in field mappings for common WhatsApp data structures
 */
export const WhatsAppFieldMappings = {
  messages: {
    'key.id': 'messageId',
    'key.remoteJid': 'chatId',
    'key.fromMe': 'fromMe',
    'key.participant': 'participant',
    'messageTimestamp': {
      target: 'timestamp',
      transform: (ts: number) => new Date(parseInt(ts.toString()) * 1000)
    },
    'pushName': 'senderName',
    'message.conversation': 'text',
    'message.extendedTextMessage.text': 'text',
    'message.imageMessage': 'imageMessage',
    'message.videoMessage': 'videoMessage',
    'message.audioMessage': 'audioMessage',
    'message.documentMessage': 'documentMessage'
  },
  
  chats: {
    'id': 'chatId',
    'name': 'name',
    'isGroup': 'isGroup',
    'unreadCount': 'unreadCount',
    'lastMessageTimestamp': {
      target: 'lastMessageAt',
      transform: (ts: number) => new Date(parseInt(ts.toString()) * 1000)
    }
  },
  
  contacts: {
    'id': 'contactId',
    'name': 'name',
    'pushname': 'pushName',
    'short': 'shortName',
    'isMe': 'isMe',
    'isUser': 'isUser',
    'isWAContact': 'isWhatsAppContact'
  }
};

/**
 * Built-in transformers for common data types
 */
export const CommonTransformers = {
  timestamp: (value: any): Date => {
    if (value instanceof Date) return value;
    if (typeof value === 'number') return new Date(value * 1000);
    if (typeof value === 'string') return new Date(value);
    return new Date();
  },
  
  phone: (value: string): string => {
    return value.replace(/[^\d+]/g, '');
  },
  
  chatId: (value: string): string => {
    return value.includes('@') ? value : `${value}@c.us`;
  },
  
  messageType: (value: any, document: any): string => {
    if (document.message?.conversation) return 'text';
    if (document.message?.extendedTextMessage) return 'text';
    if (document.message?.imageMessage) return 'image';
    if (document.message?.videoMessage) return 'video';
    if (document.message?.audioMessage) return 'audio';
    if (document.message?.documentMessage) return 'document';
    if (document.message?.stickerMessage) return 'sticker';
    if (document.message?.locationMessage) return 'location';
    return 'unknown';
  }
};